import React, { useState, useEffect, useMemo } from 'react';
import axios from '../api';
import {
  Box,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import ListPagination, { useListPagination } from '../components/ui/ListPagination';
import AddCustomerForm from '../components/AddCustomerForm';
import EditCustomerForm from '../components/EditCustomerForm';
import PageHeader from '../components/ui/PageHeader';
import ContentCard from '../components/ui/ContentCard';
import PageLoader from '../components/ui/PageLoader';
import SortableTableHeader from '../components/ui/SortableTableHeader';
import { sortItems, toggleSort } from '../utils/tableSort';
import { customerMatchesSearch } from '../utils/searchUtils';
import { pageContainerSx, tableHeaderSx, filterFieldSx, customerNameSx, metaTextSx, metaMonoSx } from '../styles/tokens';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState('asc');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/customer');
      setCustomers(response.data);
    } catch (err) {
      // Optionally show error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleSort = (column) => {
    const next = toggleSort(sortBy, sortDir, column);
    setSortBy(next.sortBy);
    setSortDir(next.sortDir);
  };

  const filteredCustomers = useMemo(() => {
    const filtered = customers.filter((c) => customerMatchesSearch(c, searchTerm));
    return sortItems(filtered, sortBy, sortDir);
  }, [customers, searchTerm, sortBy, sortDir]);

  const {
    paginatedItems: paginatedCustomers,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalCount,
  } = useListPagination(filteredCustomers, [searchTerm, sortBy, sortDir, customers.length]);

  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setEditModalOpen(true);
  };

  return (
    <Box sx={pageContainerSx}>
      <PageHeader
        title="Customers"
        subtitle="Manage customer contacts and company profiles"
        actionLabel="Add Customer"
        actionIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onAction={() => setAddModalOpen(true)}
      />

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', gap: 1.25 }}>
        <TextField
          placeholder="Search by ID, name, company, phone, email, place, address..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1, minWidth: 0, maxWidth: { xs: '100%', sm: 320 }, ...filterFieldSx }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <ContentCard sx={{ width: '100%', minWidth: 0 }}>
        {loading ? (
          <PageLoader variant="section" message="Loading customers..." />
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
            {filteredCustomers.length === 0 ? (
              <Typography align="center" sx={{ py: 4, color: 'text.secondary', fontSize: '13px' }}>
                No customers available.
              </Typography>
            ) : (
              paginatedCustomers.map((c) => (
                <Paper
                  key={c.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography component="span" sx={metaMonoSx}>
                        #{c.id}
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => navigate(`/customers/${c.id}`)}
                        sx={{ ...customerNameSx, display: 'block', textAlign: 'left' }}
                      >
                        {c.name}
                      </Button>
                      {c.company_name && (
                        <Typography sx={{ ...metaTextSx, mt: 0.25 }}>
                          {c.company_name}
                        </Typography>
                      )}
                      <Box sx={{ mt: 0.75, ...metaTextSx }}>
                        {c.ph1 && <Typography component="span" sx={metaMonoSx}>{c.ph1}</Typography>}
                        {c.email && <Typography component="span" sx={metaTextSx}> · {c.email}</Typography>}
                        {c.place && <Typography component="span" sx={metaTextSx}> · {c.place}</Typography>}
                      </Box>
                      {c.address && (
                        <Typography sx={{ ...metaTextSx, mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.address}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/customers/${c.id}`); }} sx={{ width: 28, height: 28 }} title="View">
                        <VisibilityIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditModal(c); }} sx={{ width: 28, height: 28 }} title="Edit">
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <SortableTableHeader label="ID" column="id" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} minWidth={64} />
                  <SortableTableHeader label="Name" column="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} minWidth={120} />
                  <SortableTableHeader label="Company" column="company_name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} minWidth={110} />
                  <SortableTableHeader label="Phone 1" column="ph1" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} minWidth={100} />
                  <SortableTableHeader label="Phone 2" column="ph2" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} minWidth={100} />
                  <SortableTableHeader label="Email" column="email" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} minWidth={120} />
                  <SortableTableHeader label="Place" column="place" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} minWidth={90} />
                  <SortableTableHeader label="Address" column="address" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} minWidth={140} />
                  <TableCell align="center" sx={{ ...tableHeaderSx, width: 88 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No customers available.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCustomers.map((c) => (
                    <TableRow
                      key={c.id}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <TableCell sx={{ px: 2, py: 1.5 }}>
                        <Typography component="span" sx={metaMonoSx}>
                          #{c.id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1.5 }}>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => navigate(`/customers/${c.id}`)}
                          sx={customerNameSx}
                        >
                          {c.name}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1.5 }}>
                        <Typography component="span" sx={metaTextSx}>{c.company_name || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1.5 }}>
                        <Typography component="span" sx={metaMonoSx}>{c.ph1}</Typography>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1.5 }}>
                        <Typography component="span" sx={metaMonoSx}>{c.ph2 || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1.5 }}>
                        <Typography component="span" sx={metaTextSx}>{c.email || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1.5 }}>
                        <Typography component="span" sx={metaTextSx}>{c.place || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1.5, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography component="span" sx={metaTextSx}>{c.address || '—'}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ px: 2, py: 1.5 }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(`/customers/${c.id}`); }}
                            sx={{
                              width: 28,
                              height: 28,
                              color: 'text.secondary',
                              borderRadius: 1.25,
                              '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
                            }}
                            title="View details"
                          >
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); openEditModal(c); }}
                            sx={{
                              width: 28,
                              height: 28,
                              color: 'text.secondary',
                              borderRadius: 1.25,
                              '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
                            }}
                            title="Edit"
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && (
          <ListPagination
            page={page}
            totalCount={totalCount}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            itemLabel="customer"
          />
        )}
      </ContentCard>

      {addModalOpen && (
        <AddCustomerForm onAdd={fetchCustomers} closeModal={() => setAddModalOpen(false)} />
      )}
      {editModalOpen && selectedCustomer && (
        <EditCustomerForm
          customer={selectedCustomer}
          onUpdate={fetchCustomers}
          closeModal={() => setEditModalOpen(false)}
        />
      )}
    </Box>
  );
};

export default Customers;
